// SymbolIcon · renders a real logo / flag / metal SVG for a tradeable symbol.
// Every variant is rendered as a perfect circle · no square tiles.
// Falls back to a lucide icon (passed in) if the remote logo fails to load.
import { useState } from "react";
import { resolveSymbolIcon } from "@/lib/symbolIcons";

// Inline metal SVG · radial gradient coin per metal type.
function MetalCoin({ metal, size = 28 }) {
  const palettes = {
    gold:      { hi: "#FFE9A8", mid: "#E5B453", lo: "#7B4F12", ring: "#3a2407" },
    silver:    { hi: "#F7F7FB", mid: "#B9BEC9", lo: "#54585F", ring: "#1f2128" },
    platinum:  { hi: "#EDF6F7", mid: "#9FB4BB", lo: "#3F525B", ring: "#192025" },
    palladium: { hi: "#F1ECE2", mid: "#B3A88F", lo: "#5B5141", ring: "#241e14" },
  };
  const p = palettes[metal] || palettes.gold;
  const id = `mc-${metal}`;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden style={{ display: "block" }}>
      <defs>
        <radialGradient id={id} cx="35%" cy="30%" r="80%">
          <stop offset="0%"  stopColor={p.hi} />
          <stop offset="55%" stopColor={p.mid} />
          <stop offset="100%" stopColor={p.lo} />
        </radialGradient>
      </defs>
      <circle cx="12" cy="12" r="11.4" fill={`url(#${id})`} stroke={p.ring} strokeWidth="0.8" />
      <circle cx="12" cy="12" r="7.5" fill="none" stroke={p.hi} strokeOpacity="0.55" strokeWidth="0.5" />
    </svg>
  );
}

// Inline energy SVG · oil drop or gas flame rendered inside a perfect circle.
function EnergyMark({ energy, size = 28 }) {
  const id = `eg-${energy}`;
  if (energy === "gas") {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden style={{ display: "block" }}>
        <defs>
          <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"  stopColor="#FFE38A" />
            <stop offset="55%" stopColor="#FF9D3A" />
            <stop offset="100%" stopColor="#C13B12" />
          </linearGradient>
          <radialGradient id={`${id}-bg`} cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#5a2400" />
            <stop offset="100%" stopColor="#1a0a00" />
          </radialGradient>
        </defs>
        <circle cx="12" cy="12" r="11.4" fill={`url(#${id}-bg)`} stroke="#FF9D3A" strokeWidth="0.6" strokeOpacity="0.7" />
        <path d="M12 4.5c0 3.6-3.6 4.5-3.6 8.5a3.6 3.6 0 1 0 7.2 0c0-2.4-1.2-3.2-2-4.8-0.8 1.2-1.6 1.2-1.6-3.7z"
              fill={`url(#${id})`} stroke="#7a1f04" strokeWidth="0.5" />
      </svg>
    );
  }
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden style={{ display: "block" }}>
      <defs>
        <radialGradient id={`${id}-bg`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#3a2810" />
          <stop offset="100%" stopColor="#120a00" />
        </radialGradient>
        <radialGradient id={id} cx="40%" cy="35%" r="75%">
          <stop offset="0%"  stopColor="#8a6432" />
          <stop offset="55%" stopColor="#241406" />
          <stop offset="100%" stopColor="#080300" />
        </radialGradient>
      </defs>
      <circle cx="12" cy="12" r="11.4" fill={`url(#${id}-bg)`} stroke="#c08540" strokeWidth="0.6" strokeOpacity="0.55" />
      <path d="M12 5c-2.8 4-5.6 6.8-5.6 10a5.6 5.6 0 1 0 11.2 0c0-3.2-2.8-6-5.6-10z"
            fill={`url(#${id})`} stroke="#000" strokeWidth="0.5" />
      <ellipse cx="10" cy="11.4" rx="1.3" ry="0.8" fill="#fff" opacity="0.5" />
    </svg>
  );
}

// Country-flag chip · uses the HatScripts circle-flags SVG set which are
// natively round (no cropping of rectangles into circles).
function CircularFlag({ cc, size = 26, alt = "", ring = true }) {
  return (
    <img
      src={`https://cdn.jsdelivr.net/gh/HatScripts/circle-flags@gh-pages/flags/${cc}.svg`}
      width={size}
      height={size}
      alt={alt}
      loading="lazy"
      style={{
        width: size,
        height: size,
        display: "block",
        borderRadius: "50%",
        boxShadow: ring ? "0 0 0 1px color-mix(in srgb, var(--text-1) 18%, transparent)" : "none",
      }}
      onError={(e) => { e.currentTarget.style.visibility = "hidden"; }}
    />
  );
}

// Two overlapping circular flags for an FX pair (base bottom-left, quote top-right).
function FlagPair({ a, b, size = 28 }) {
  const r = Math.round(size * 0.64);
  return (
    <span
      className="relative inline-block"
      style={{ width: size, height: size }}
      aria-hidden
    >
      <span className="absolute" style={{ left: 0, bottom: 0 }}>
        <CircularFlag cc={a} size={r} />
      </span>
      <span
        className="absolute rounded-full"
        style={{
          right: 0, top: 0,
          padding: 1.5,
          background: "var(--surface)",
        }}
      >
        <CircularFlag cc={b} size={r} ring={false} />
      </span>
    </span>
  );
}

// Public component · always renders into a perfectly round badge.
// `size` is the outer width/height (default 28 px).
export default function SymbolIcon({ symbol, category, Fallback, size = 28 }) {
  const [stage, setStage] = useState(0); // 0 = primary, 1 = fallbackSrc, 2 = lucide
  const info = resolveSymbolIcon({ symbol, category });

  // Walks the fallback chain for stock logos: Clearbit → Google favicon → lucide.
  const fallbackChain =
    info && info.kind === "stock" ? [info.src, info.fallbackSrc].filter(Boolean) :
    info && info.kind === "crypto" ? [info.src] : [];

  // Stock logos sit on a white circular disc so the brand mark stays crisp
  // in both dark and light mode.
  const stockTile = (children) => (
    <span
      className="rounded-full grid place-items-center shrink-0 overflow-hidden"
      style={{
        width: size,
        height: size,
        background: "#fff",
        boxShadow: "0 0 0 1px color-mix(in srgb, var(--brand) 14%, transparent)",
      }}
      aria-hidden
    >
      {children}
    </span>
  );

  // Crypto / flag / metal / energy variants are themselves circular ·
  // we render them naked without any surrounding tile.
  const naked = (children) => (
    <span className="shrink-0 inline-flex" style={{ width: size, height: size }} aria-hidden>
      {children}
    </span>
  );

  // Final lucide fallback inside a subtle round disc.
  if (!info || (fallbackChain.length > 0 && stage >= fallbackChain.length)) {
    return (
      <span
        className="rounded-full grid place-items-center shrink-0"
        style={{
          width: size, height: size,
          background: "color-mix(in srgb, var(--brand) 10%, transparent)",
          boxShadow: "0 0 0 1px color-mix(in srgb, var(--brand) 20%, transparent)",
        }}
        aria-hidden
      >
        {Fallback
          ? <Fallback size={Math.round(size * 0.48)} strokeWidth={1.8} style={{ color: "var(--brand)" }} />
          : <span className="text-[12px] font-semibold text-text-2">{(symbol || "").slice(0, 2)}</span>}
      </span>
    );
  }

  if (info.kind === "crypto") {
    return naked(
      <img
        src={fallbackChain[stage]}
        alt={symbol}
        width={size}
        height={size}
        loading="lazy"
        style={{ width: size, height: size, objectFit: "contain", borderRadius: "50%" }}
        onError={() => setStage((s) => s + 1)}
      />
    );
  }

  if (info.kind === "stock") {
    const inner = Math.round(size * 0.74);
    return stockTile(
      <img
        src={fallbackChain[stage]}
        alt={info.alt || symbol}
        width={inner}
        height={inner}
        loading="lazy"
        style={{ width: inner, height: inner, objectFit: "contain" }}
        onError={() => setStage((s) => s + 1)}
      />
    );
  }

  if (info.kind === "flagpair") {
    return naked(<FlagPair a={info.a} b={info.b} size={size} />);
  }

  if (info.kind === "flag") {
    return naked(<CircularFlag cc={info.cc} alt={info.alt || symbol} size={size} />);
  }

  if (info.kind === "metal") {
    return naked(<MetalCoin metal={info.metal} size={size} />);
  }

  if (info.kind === "energy") {
    return naked(<EnergyMark energy={info.energy} size={size} />);
  }

  return naked(null);
}
